<div [ngClass]="isEmbedded ? 'bg-transparent font-sans text-on-surface pb-6 pt-1' : 'min-h-screen bg-background font-sans text-on-surface pb-10 pt-4'">
  <div class="loader-overlay" *ngIf="loading">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
  </div>

  <main class="pd-main-wrapper" [class.embedded-view]="isEmbedded" *ngIf="!loading && profileDetails">
    
    <!-- Back Button (hidden when embedded in RM workspace) -->
    <a *ngIf="!isEmbedded" [routerLink]="isLoggedIn ? '/dashboard' : '/'" class="inline-flex items-center gap-1 text-primary text-xs font-bold mb-3 hover:underline no-underline">
      <span class="material-symbols-outlined text-[14px]">arrow_back</span> Back to Matches
    </a>

    <!-- 1. COMPACT HERO SECTION -->
    <div class="pd-hero-card">
      <div class="pd-hero-grid">
        
        <!-- LEFT: Photo & Gallery (~240px wide) -->
        <div class="flex flex-col">
          <!-- Main Photo: Compressed 260px height -->
          <div class="pd-main-photo group relative rounded-2xl overflow-hidden bg-surface-container-low" [class.cursor-pointer]="profileDetails?.blurPhotos && selectedMainImage" (click)="profileDetails?.blurPhotos && selectedMainImage ? onLockedGalleryClick() : null">
            <ng-container *ngIf="selectedMainImage || base64Image; else defaultProfilePhoto">
              <img [src]="selectedMainImage ? 'data:image/jpeg;base64,' + selectedMainImage : (base64Image ? 'data:image/jpeg;base64,' + base64Image : defaultAvatarUrl)" class="w-full h-full object-cover transition-all duration-300" [class.blur-md]="profileDetails?.blurPhotos && selectedMainImage" />
            </ng-container>
            <ng-template #defaultProfilePhoto>
              <img [src]="defaultAvatarUrl" alt="Default profile" class="w-full h-full object-cover" />
            </ng-template>
            <div *ngIf="!isLoggedIn" class="absolute inset-0 flex flex-col items-center justify-center bg-black/40 backdrop-blur-[3px] text-white p-3 text-center rounded-2xl">
              <span class="material-symbols-outlined text-amber-400 text-3xl mb-1 shadow-sm">lock</span>
              <span class="text-[11px] font-extrabold uppercase tracking-wider">Sign in required</span>
            </div>
            <div *ngIf="profileDetails?.blurPhotos && selectedMainImage" class="absolute inset-0 flex flex-col items-center justify-center bg-black/35 backdrop-blur-[1.5px] text-amber-300 p-3 text-center rounded-2xl">
              <span class="material-symbols-outlined text-4xl mb-2">lock</span>
              <span class="text-[12px] font-semibold uppercase tracking-wider">Upgrade to unlock</span>
            </div>
          </div>
          
          <!-- Thumbnail Strip (DP + 4 Gallery slots) -->
          <div class="pd-thumb-strip scrollbar-none flex gap-1.5 mt-2 overflow-x-auto pb-1">
            <!-- Slot 0: Main DP -->
            <div (click)="onGalleryThumbnailClick(0)" class="pd-thumb border-2 ring-1 ring-primary/20 cursor-pointer flex-shrink-0 aspect-square rounded-lg overflow-hidden relative" [class.border-primary]="!selectedMainImage" [class.border-transparent]="selectedMainImage">
              <ng-container *ngIf="base64Image; else galDefault">
                <img [src]="'data:image/jpeg;base64,' + base64Image" class="w-full h-full object-cover" [class.blur-sm]="!isLoggedIn" />
              </ng-container>
              <ng-template #galDefault>
                <img [src]="defaultAvatarUrl" alt="Default profile" class="w-full h-full object-cover" />
              </ng-template>
            </div>

            <!-- Slots 1-4: Uploaded Gallery Photos -->
            <div *ngFor="let photoName of galleryPhotos; let i = index" (click)="onGalleryThumbnailClick(i + 1, photoName)" class="pd-thumb border-2 cursor-pointer flex-shrink-0 aspect-square rounded-lg overflow-hidden relative bg-surface-container-low" [class.border-primary]="selectedMainImage === galleryImagesBase64[photoName]" [class.border-transparent]="selectedMainImage !== galleryImagesBase64[photoName]">
              <img *ngIf="galleryImagesBase64[photoName]" [src]="'data:image/jpeg;base64,' + galleryImagesBase64[photoName]" class="w-full h-full object-cover transition-transform hover:scale-105" [class.blur-sm]="profileDetails?.blurPhotos" />
              <div *ngIf="!galleryImagesBase64[photoName]" class="w-full h-full flex items-center justify-center bg-surface-container">
                <span class="material-symbols-outlined text-xs animate-spin text-primary/40">progress_activity</span>
              </div>
              <div *ngIf="profileDetails?.blurPhotos" class="absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-[1.5px] text-amber-300">
                <span class="material-symbols-outlined text-xs shadow">lock</span>
              </div>
            </div>

            <!-- Empty Remaining Slots up to 4 -->
            <div *ngFor="let i of [1,2,3,4].slice(galleryPhotos.length)" (click)="onGalleryThumbnailClick(999)" class="pd-thumb border border-dashed border-secondary/30 flex-shrink-0 aspect-square rounded-lg flex items-center justify-center bg-surface-container/50 text-secondary/30 cursor-pointer hover:border-primary/40 relative">
              <span *ngIf="!profileDetails?.blurPhotos" class="material-symbols-outlined text-[13px]">image</span>
              <div *ngIf="profileDetails?.blurPhotos" class="w-full h-full flex items-center justify-center bg-black/10 text-amber-800/60">
                <span class="material-symbols-outlined text-[12px]">lock</span>
              </div>
            </div>
          </div>
        </div>

        <!-- RIGHT: Summary & Actions -->
        <div class="pd-hero-right">
           <div>
             <!-- Name & Status Header Row -->
             <div class="flex flex-wrap items-center justify-between gap-2 border-b border-secondary/10 pb-2.5 mb-2.5">
               <div>
                 <div class="flex items-center gap-2 flex-wrap">
                   <h1 class="font-display font-bold text-2xl text-primary m-0">{{ getMaskedName() }}</h1>
                   <span *ngIf="profileDetails?.verify?.id_proof_verify_status" class="inline-flex items-center gap-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200/50 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider">
                     <span class="material-symbols-outlined fill-icon text-[13px]">verified</span> Verified
                   </span>
                   <span *ngIf="profileDetails?.is_broker_profile && profileDetails?.claim_status !== 'claimed'" class="inline-flex items-center gap-0.5 bg-amber-50 text-amber-800 border border-amber-300 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider">
                     <span class="material-symbols-outlined text-[13px] text-amber-700">handshake</span> Broker Listed
                   </span>
                 </div>
                 <div class="flex items-center gap-2 mt-1 text-xs text-secondary/70 font-medium">
                   <span class="font-mono font-semibold">ID: ET-{{ profileDetails._id | slice:-5 }}</span>
                   <span>•</span>
                   <ng-container *ngIf="isLoggedIn && isOnline(); else profileOffline">
                     <span class="inline-flex items-center gap-1 text-emerald-600 font-semibold">
                       <span class="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse"></span> Online Now
                     </span>
                   </ng-container>
                   <ng-template #profileOffline>
                     <span>{{ timeAgo() === 'Unknown' ? 'Offline' : 'Last seen ' + timeAgo() }}</span>
                   </ng-template>
                 </div>
               </div>

               <!-- Primary Actions Row -->
               <div class="flex flex-wrap items-center gap-2">
                 <button (click)="addToFavorites()" class="h-8 w-8 rounded-lg border border-primary/20 flex items-center justify-center transition-colors shadow-2xs cursor-pointer" [ngClass]="isFavorited ? 'bg-primary text-white' : 'bg-white text-primary hover:bg-primary/5'" [title]="isFavorited ? 'Remove from Favorites' : 'Add to Favorites'">
                     <span class="material-symbols-outlined text-[16px]" [class.fill-icon]="isFavorited" [class.favorite-animate]="animateFavorite">bookmark</span>
                 </button>
                 <button (click)="shareProfile()" class="h-8 w-8 rounded-lg border border-primary/20 flex items-center justify-center text-primary hover:bg-primary/5 transition-colors bg-white shadow-2xs cursor-pointer" title="Share Profile">
                    <span class="material-symbols-outlined text-[16px]">share</span>
                 </button>
                 <button *ngIf="isLoggedIn && profileId !== myProfileId" (click)="openReportModal()" class="h-8 w-8 rounded-lg border border-red-200 flex items-center justify-center text-red-600 hover:bg-red-50 transition-colors bg-white shadow-2xs cursor-pointer" title="Report Profile">
                    <span class="material-symbols-outlined text-[16px]">flag</span>
                 </button>
                 
                 <ng-container *ngIf="isLoggedIn; else loginBtn">
                    <button *ngIf="!currentInterestStatus" (click)="sendInterest()" class="h-8 px-4 bg-primary text-white rounded-lg text-xs font-bold shadow-2xs hover:bg-primary/90 transition-all flex items-center gap-1 border-0 cursor-pointer">
                      <span class="material-symbols-outlined text-[15px]">favorite</span> Connect
                    </button>
                    <button *ngIf="currentInterestStatus === 'Pending' && !isReceiver" class="h-8 px-4 bg-surface-container-highest text-on-surface-variant cursor-not-allowed rounded-lg text-xs font-bold flex items-center gap-1 border-0">
                      <span class="material-symbols-outlined text-[15px]">pending</span> Pending
                    </button>
                    <div *ngIf="currentInterestStatus === 'Pending' && isReceiver" class="flex gap-1.5">
                      <button (click)="respondToInterest('Accepted')" class="h-8 px-3 bg-primary text-white rounded-lg text-xs font-bold shadow-2xs hover:bg-primary/90 flex items-center gap-1 border-0 cursor-pointer">
                        <span class="material-symbols-outlined text-[14px]">check</span> Accept
                      </button>
                      <button (click)="respondToInterest('Denied')" class="h-8 px-3 bg-white border border-primary text-primary rounded-lg text-xs font-bold hover:bg-primary/5 flex items-center gap-1 cursor-pointer">
                        <span class="material-symbols-outlined text-[14px]">close</span> Decline
                      </button>
                    </div>
                    <ng-container *ngIf="currentInterestStatus === 'Accepted'">
                      <a *ngIf="!profileDetails?.isChatLocked" routerLink="/chat" [queryParams]="{receiverId: profileId}" class="h-8 px-4 bg-success text-white rounded-lg text-xs font-bold shadow-2xs hover:bg-success/90 transition-all flex items-center gap-1 no-underline cursor-pointer">
                        <span class="material-symbols-outlined text-[15px]">chat</span> Chat
                      </a>
                      <button *ngIf="profileDetails?.isChatLocked" (click)="onLockedChatClick()" class="h-8 px-3.5 bg-neutral-900 text-rose-200 border border-rose-500/40 rounded-lg text-xs font-bold shadow-2xs hover:bg-neutral-800 transition-all flex items-center gap-1 cursor-pointer">
                        <span class="material-symbols-outlined text-[14px] text-rose-400">lock</span> Locked
                      </button>
                    </ng-container>
                 </ng-container>
                 <ng-template #loginBtn>
                    <a routerLink="/login" class="h-8 px-4 bg-primary text-white rounded-lg text-xs font-bold shadow-2xs hover:bg-primary/90 transition-all flex items-center gap-1 no-underline cursor-pointer">
                      <span class="material-symbols-outlined text-[15px]">lock</span> Login
                    </a>
                 </ng-template>
               </div>
             </div>

             <!-- Phase 3 Quota / Access Gating VIP Banner -->
             <div *ngIf="profileDetails?.isContactLocked" class="bg-gradient-to-r from-amber-500/15 via-pink-500/10 to-purple-500/15 rounded-2xl p-3.5 border border-amber-400/50 flex flex-wrap items-center justify-between gap-3 my-3.5 shadow-sm">
               <div class="flex items-center gap-2.5">
                 <span class="material-symbols-outlined text-amber-600 text-2xl">workspace_premium</span>
                 <div>
                   <h4 class="font-display font-extrabold text-xs text-[#3a101e] m-0 flex items-center gap-1.5">
                     <span>{{ profileDetails.blurPhotos ? 'Direct Contacts & Gallery Photos Restricted' : 'Direct Contacts Restricted' }}</span>
                     <span class="bg-amber-500 text-white text-[9px] px-1.5 py-0.5 rounded uppercase font-mono tracking-widest">{{ profileDetails.userPlan || 'Free' }} Plan</span>
                   </h4>
                   <p class="text-[11px] text-secondary/80 m-0 mt-0.5">{{ profileDetails.upgradeRequiredMessage }}</p>
                 </div>
               </div>
               <a routerLink="/plans" class="px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-extrabold text-xs rounded-xl shadow-md uppercase tracking-wider no-underline transition-all hover:scale-105 shrink-0">
                 Unlock VIP Access
               </a>
             </div>

             <!-- Compact Chips Attributes -->
             <div class="pd-chip-list">
               <span class="pd-chip">
                 <span class="material-symbols-outlined">person</span>
                 {{ getAge(profileDetails.basic_info?.date_of_birth) }} Yrs • {{ profileDetails.personal_info?.height ? profileDetails.personal_info.height + ' cm' : '--' }}
               </span>
               <span class="pd-chip">
                 <span class="material-symbols-outlined">temple_hindu</span>
                 {{ getDisplayValue(profileDetails.religion_info?.religion_id, '--') }}, {{ getDisplayValue(profileDetails.religion_info?.caste_id, '--') }}
               </span>
               <span class="pd-chip">
                 <span class="material-symbols-outlined">work</span>
                 {{ getProfessionLine() !== '--' ? getProfessionLine() : 'Professional' }}
               </span>
               <span class="pd-chip">
                 <span class="material-symbols-outlined">location_on</span>
                 {{ getLocationLine() }}
               </span>
               <span class="pd-chip">
                 <span class="material-symbols-outlined">school</span>
                 {{ getDisplayValue(profileDetails.professional_info?.education_id, '--') }}
               </span>
             </div>

             <!-- About Candidate Summary -->
             <div *ngIf="profileDetails.personal_info?.about_me || profileDetails.basic_info?.about_me" class="mt-2.5">
               <h3 class="font-display font-bold text-[11px] uppercase tracking-wider text-secondary/70 mb-1 m-0">About</h3>
               <p class="text-xs text-secondary/90 leading-normal font-serif m-0 line-clamp-2 whitespace-pre-wrap">{{ getDisplayValue(profileDetails.personal_info?.about_me || profileDetails.basic_info?.about_me, '') }}</p>
             </div>
           </div>

           <!-- Safe Matrimony Minimal Notice -->
           <div class="bg-amber-50/70 rounded-lg px-3 py-1.5 border border-amber-200/60 flex items-center gap-2 text-amber-900 text-[11px] font-medium mt-auto">
             <span class="material-symbols-outlined text-[15px] text-amber-700">shield_person</span>
             <span>Safe Matrimony: Always verify details independently. Report suspicious behavior.</span>
           </div>
           
        </div>
      </div>
    </div>

    <!-- 2. DESKTOP 2x2 CARD GRID (Uniform Executive SaaS Property Grid!) -->
    <div class="pd-info-grid">
       
      <!-- LEFT COLUMN CONTAINER -->
      <div class="flex flex-col gap-4">

        <!-- CARD 0: Verified Contact Information -->
        <div class="pd-info-card border-l-4 border-l-primary bg-primary/5">
          <div class="pd-card-header text-primary font-bold">
            <span class="material-symbols-outlined fill-icon">contact_phone</span> Contact Information
          </div>
          <div class="pd-property-grid">
            <div class="pd-prop">
              <span class="pd-prop-label flex items-center gap-1"><span class="material-symbols-outlined text-[13px]">call</span> Mobile Number</span>
              <span class="pd-prop-val font-mono font-bold text-sm" [ngClass]="{'text-amber-800': !isLoggedIn || profileDetails?.isContactLocked, 'text-primary': isLoggedIn && !profileDetails?.isContactLocked}">
                {{ !isLoggedIn ? '+91 XXXXX XXXXX (Login)' : (profileDetails?.basic_info?.mobile || profileDetails?.basic_info?.contact_number || '--') }}
              </span>
            </div>
            <div class="pd-prop">
              <span class="pd-prop-label flex items-center gap-1"><span class="material-symbols-outlined text-[13px]">mail</span> Email Address</span>
              <span class="pd-prop-val font-mono text-sm" [ngClass]="{'text-amber-800': !isLoggedIn || profileDetails?.isContactLocked}">
                {{ !isLoggedIn ? 'xxxx@xxxx.com (Login)' : (profileDetails?.basic_info?.email || '--') }}
              </span>
            </div>
          </div>
          <div *ngIf="isLoggedIn && profileDetails?.isContactLocked" class="mt-3 flex flex-col sm:flex-row items-center justify-between bg-gradient-to-r from-amber-50/95 via-orange-50/60 to-amber-50/95 border border-amber-200/80 rounded-xl p-3 gap-3 shadow-2xs">
            <div class="flex items-center gap-2.5">
              <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shrink-0 shadow-2xs text-white">
                <span class="material-symbols-outlined text-[17px]">lock</span>
              </div>
              <div class="text-xs">
                <span class="font-bold text-amber-950 block">Verified Contact Details Locked</span>
                <span class="text-amber-800/90">Upgrade your plan to instantly unlock direct phone numbers and email access.</span>
              </div>
            </div>
            <a routerLink="/plans" class="shrink-0 px-3.5 py-1.5 bg-gradient-to-r from-amber-600 to-yellow-600 hover:from-amber-700 hover:to-yellow-700 text-white rounded-lg text-xs font-extrabold shadow-sm flex items-center gap-1.5 cursor-pointer no-underline transition-transform hover:scale-105">
              <span class="material-symbols-outlined text-[15px]">workspace_premium</span> Upgrade Plan
            </a>
          </div>
        </div>

        <!-- CARD 1: Basic & Location Details -->
        <div class="pd-info-card">
          <div class="pd-card-header">
            <span class="material-symbols-outlined">person_book</span> Basic & Location Details
          </div>
          <div class="pd-property-grid">
            <div class="pd-prop">
              <span class="pd-prop-label">City</span>
              <span class="pd-prop-val">{{ getDisplayValue(profileDetails.residing_location?.city_id, '--') }}</span>
            </div>
            <div class="pd-prop">
              <span class="pd-prop-label">Weight</span>
              <span class="pd-prop-val" [ngClass]="{'opacity-50 blur-[2px]': !isLoggedIn}">
                {{ isLoggedIn ? (profileDetails.personal_info?.weight ? profileDetails.personal_info.weight + ' kg' : '--') : 'Private' }}
              </span>
            </div>
            <div class="pd-prop">
              <span class="pd-prop-label">State / Country</span>
              <span class="pd-prop-val">{{ getDisplayValue(profileDetails.residing_location?.state_id, '--') }}, {{ getDisplayValue(profileDetails.residing_location?.country_id, '--') }}</span>
            </div>
            <div class="pd-prop">
              <span class="pd-prop-label">Marital Status</span>
              <span class="pd-prop-val">{{ getDisplayValue(profileDetails.personal_info?.marital_status, '--') }}</span>
            </div>
            <div class="pd-prop">
              <span class="pd-prop-label">Mother Tongue</span>
              <span class="pd-prop-val">{{ getDisplayValue(profileDetails.basic_info?.mother_lang_id, '--') }}</span>
            </div>
            <div class="pd-prop">
              <span class="pd-prop-label">Physical Status</span>
              <span class="pd-prop-val">{{ getDisplayValue(profileDetails.personal_info?.physical_status, '--') }}</span>
            </div>
            <div class="pd-prop">
              <span class="pd-prop-label">Citizenship</span>
              <span class="pd-prop-val" [ngClass]="{'opacity-50 blur-[2px]': !isLoggedIn}">
                {{ isLoggedIn ? getDisplayValue(profileDetails.personal_info?.citizenship_id, '--') : 'Private' }}
              </span>
            </div>
          </div>
        </div>

        <!-- CARD 2: Education & Profession -->
        <div class="pd-info-card">
          <div class="pd-card-header">
            <span class="material-symbols-outlined">work</span> Education & Profession
          </div>
          <div class="pd-property-grid">
            <div class="pd-prop">
              <span class="pd-prop-label">Qualification</span>
              <span class="pd-prop-val">{{ getDisplayValue(profileDetails.professional_info?.education_id, '--') }}</span>
            </div>
            <div class="pd-prop" *ngIf="profileDetails.professional_info?.education_detail">
              <span class="pd-prop-label">College / Institution</span>
              <span class="pd-prop-val">{{ profileDetails.professional_info.education_detail }}</span>
            </div>
            <div class="pd-prop">
              <span class="pd-prop-label">Occupation</span>
              <div class="pd-prop-val flex flex-col gap-0.5" *ngIf="profileDetails.professional_info?.occupation_detail; else singleOccupation">
                <span>• {{ getDisplayValue(profileDetails.professional_info?.occupation_id, '--') }}</span>
                <span>• {{ profileDetails.professional_info.occupation_detail }}</span>
              </div>
              <ng-template #singleOccupation>
                <span class="pd-prop-val">{{ getDisplayValue(profileDetails.professional_info?.occupation_id, '--') }}</span>
              </ng-template>
            </div>
            <div class="pd-prop">
              <span class="pd-prop-label">Sector</span>
              <span class="pd-prop-val">{{ getDisplayValue(profileDetails.professional_info?.employmentype_id, '--') }}</span>
            </div>
            <div class="pd-prop">
              <span class="pd-prop-label">Annual Earnings</span>
              <span class="pd-prop-val" *ngIf="canViewAnnualEarnings">
                {{ profileDetails.professional_info?.annual_salary ? '₹' + (profileDetails.professional_info.annual_salary | number) : getDisplayValue(profileDetails.professional_info?.income_id, 'Not Disclosed') }}
              </span>
              <a routerLink="/plans" *ngIf="!canViewAnnualEarnings" class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/80 text-amber-950 font-bold text-xs hover:bg-amber-100/80 transition-all shadow-2xs group no-underline mt-0.5 w-fit">
                <span class="material-symbols-outlined text-[14px] text-amber-600 group-hover:scale-110 transition-transform">lock</span>
                <span>Upgrade to view</span>
              </a>
            </div>
            <div class="pd-prop" *ngIf="profileDetails.professional_info?.additional_income_source">
              <span class="pd-prop-label">Additional Income Source</span>
              <span class="pd-prop-val">{{ profileDetails.professional_info.additional_income_source }}</span>
            </div>
            <div class="pd-prop" *ngIf="profileDetails.professional_info?.additional_income_source && profileDetails.professional_info?.additional_annual_income">
              <span class="pd-prop-label">Additional Annual Income</span>
              <span class="pd-prop-val" *ngIf="canViewAnnualEarnings">₹{{ profileDetails.professional_info.additional_annual_income | number }}</span>
              <a routerLink="/plans" *ngIf="!canViewAnnualEarnings" class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/80 text-amber-950 font-bold text-xs hover:bg-amber-100/80 transition-all shadow-2xs group no-underline mt-0.5 w-fit">
                <span class="material-symbols-outlined text-[14px] text-amber-600 group-hover:scale-110 transition-transform">lock</span>
                <span>Upgrade to view</span>
              </a>
            </div>
          </div>
        </div>

      </div>

      <!-- RIGHT COLUMN CONTAINER -->
      <div class="flex flex-col gap-4">
        
        <!-- CARD 3: Religion & Horoscope (Merged!) -->
        <div class="pd-info-card">
          <div class="pd-card-header">
            <span class="material-symbols-outlined">auto_awesome</span> Religion & Horoscope
          </div>
          <div class="pd-property-grid mb-4">
            <div class="pd-prop">
              <span class="pd-prop-label">Religion</span>
              <span class="pd-prop-val">{{ getDisplayValue(profileDetails.religion_info?.religion_id, '--') }}</span>
            </div>
            <div class="pd-prop">
              <span class="pd-prop-label">Caste / Sect</span>
              <span class="pd-prop-val">{{ getDisplayValue(profileDetails.religion_info?.caste_id, '--') }}</span>
            </div>
            <div class="pd-prop">
              <span class="pd-prop-label">Sub-Caste</span>
              <span class="pd-prop-val" [ngClass]="{'opacity-50 blur-[2px]': !isLoggedIn}">
                {{ isLoggedIn ? getDisplayValue(profileDetails.religion_info?.sub_caste_id, '--') : 'Private' }}
              </span>
            </div>
            <div class="pd-prop">
              <span class="pd-prop-label">Gothra</span>
              <span class="pd-prop-val" [ngClass]="{'opacity-50 blur-[2px]': !isLoggedIn}">
                {{ isLoggedIn ? getDisplayValue(profileDetails.religion_info?.gothrm_id, '--') : 'Private' }}
              </span>
            </div>
            <div class="pd-prop">
              <span class="pd-prop-label">Star / Raasi</span>
              <span class="pd-prop-val" [ngClass]="{'opacity-50 blur-[2px]': !isLoggedIn}">
                {{ isLoggedIn ? getStarRaasiLine() : 'Private' }}
              </span>
            </div>
            <div class="pd-prop">
              <span class="pd-prop-label">Dosham</span>
              <span class="pd-prop-val" [ngClass]="{'opacity-50 blur-[2px]': !isLoggedIn}">
                {{ isLoggedIn ? getDisplayValue(profileDetails.religion_info?.dosham, 'No') : 'Private' }}
              </span>
            </div>
          </div>

          <!-- Merged Horoscope Box -->
          <div class="pt-3 border-t border-secondary/10 mt-auto">
            <div *ngIf="!isLoggedIn" class="p-2 bg-amber-50 border border-amber-200/60 rounded-xl text-xs text-amber-800 font-medium flex gap-1.5 items-center">
              <span class="material-symbols-outlined text-[15px]">lock</span>
              <span>Granular horoscope factors locked. Sign in!</span>
            </div>

            <div *ngIf="isLoggedIn">
              <div *ngIf="profileDetails.horoscope_file_url && horoscopeAccessGranted; else noHoroBox" class="flex flex-wrap items-center justify-between bg-primary/5 border border-primary/10 rounded-xl p-2.5 gap-2">
                <div class="flex items-center gap-2">
                  <span class="material-symbols-outlined text-primary text-lg">description</span>
                  <span class="font-bold text-xs text-primary">Horoscope Available</span>
                </div>
                <div class="flex gap-1.5">
                  <button (click)="viewBiodata()" class="px-2.5 py-1 bg-white hover:bg-primary/5 text-primary rounded-lg text-xs font-bold shadow-2xs border border-primary/20 flex items-center gap-1 cursor-pointer">
                    <span class="material-symbols-outlined text-[13px]">visibility</span> View
                  </button>
                  <button (click)="downloadBiodata()" class="px-2.5 py-1 bg-primary hover:bg-primary/90 text-white rounded-lg text-xs font-bold shadow-2xs border-0 flex items-center gap-1 cursor-pointer">
                    <span class="material-symbols-outlined text-[13px]">download</span> Biodata
                  </button>
                </div>
              </div>
              <ng-template #noHoroBox>
                <div *ngIf="!profileDetails.horoscope_file_url" class="text-xs font-medium text-secondary/60 flex items-center gap-1.5 py-0.5">
                  <span class="material-symbols-outlined text-[15px]">info</span> No horoscope uploaded yet.
                </div>
                <div *ngIf="profileDetails.horoscope_file_url && !horoscopeAccessGranted" class="flex flex-col sm:flex-row items-center justify-between bg-gradient-to-r from-amber-50/95 via-orange-50/60 to-amber-50/95 border border-amber-200/80 rounded-xl p-3 gap-3 shadow-2xs">
                  <div class="flex items-center gap-2.5">
                    <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shrink-0 shadow-2xs text-white">
                      <span class="material-symbols-outlined text-[17px]">lock</span>
                    </div>
                    <div class="text-xs">
                      <span class="font-bold text-amber-950 block">Horoscope & Biodata Form Locked</span>
                      <span class="text-amber-800/90" *ngIf="!isGoldOrAbove">Upgrade to Gold Membership to view and download candidate horoscope & biodata forms when mutual interest is accepted.</span>
                      <span class="text-amber-800/90" *ngIf="isGoldOrAbove && currentInterestStatus !== 'Accepted'">Available exclusively once mutual interest connection request is accepted by both members.</span>
                    </div>
                  </div>
                  <button *ngIf="!isGoldOrAbove" [routerLink]="['/plans']" class="shrink-0 px-3.5 py-1.5 bg-gradient-to-r from-amber-600 to-yellow-600 hover:from-amber-700 hover:to-yellow-700 text-white rounded-lg text-xs font-extrabold shadow-sm flex items-center gap-1.5 cursor-pointer no-underline transition-transform hover:scale-105">
                    <span class="material-symbols-outlined text-[15px]">workspace_premium</span> Upgrade to Gold
                  </button>
                </div>
              </ng-template>
            </div>
          </div>
        </div>

        <!-- CARD 4: Family Details -->
        <div class="pd-info-card">
          <div class="pd-card-header">
            <span class="material-symbols-outlined">family_restroom</span> Family Details
          </div>
          <div class="pd-property-grid">
            <div class="pd-prop">
              <span class="pd-prop-label">Family Type</span>
              <span class="pd-prop-val">{{ getDisplayValue(profileDetails.family_info?.family_type, '--') }}</span>
            </div>
            <div class="pd-prop">
              <span class="pd-prop-label">Family Status</span>
              <span class="pd-prop-val">{{ getDisplayValue(profileDetails.family_info?.family_status, '--') }}</span>
            </div>
            <div class="pd-prop">
              <span class="pd-prop-label">Father's Job</span>
              <span class="pd-prop-val">{{ getDisplayValue(profileDetails.family_info?.father_occupation_id, '--') }}</span>
            </div>
            <div class="pd-prop">
              <span class="pd-prop-label">Mother's Job</span>
              <span class="pd-prop-val">{{ getDisplayValue(profileDetails.family_info?.mother_occupation_id, '--') }}</span>
            </div>
            <div class="pd-prop col-span-2 sm:col-span-1">
              <span class="pd-prop-label">Siblings</span>
              <span class="pd-prop-val">
                {{ profileDetails.family_info?.no_of_brothers || 0 }} Brother(s), {{ profileDetails.family_info?.no_of_sisters || 0 }} Sister(s)
              </span>
            </div>
          </div>
        </div>

      </div>

    </div>

    <!-- 3. SIMILAR PROFILES SECTION -->
    <div *ngIf="similarMatches.length > 0" class="mt-6 pt-5 border-t border-secondary/10">
      <div class="flex items-center justify-between mb-3.5">
        <h2 class="font-display font-bold text-base text-primary flex items-center gap-1.5 m-0">
          <span class="material-symbols-outlined text-[19px]">group</span> Similar Profiles
        </h2>
        <a routerLink="/profiles" class="text-xs font-bold text-primary hover:underline flex items-center no-underline">
          See all <span class="material-symbols-outlined text-[14px]">arrow_forward</span>
        </a>
      </div>
      
      <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3.5">
        <a *ngFor="let profile of similarMatches" [routerLink]="['/profile-details', profile._id]" class="group bg-white rounded-xl border border-primary/10 shadow-2xs hover:shadow-md transition-all overflow-hidden flex flex-col no-underline">
          <div class="relative aspect-[4/5] w-full overflow-hidden bg-primary-fixed">
            <ng-container *ngIf="base64Images[profile._id]; else defSim">
                <img [src]="'data:image/jpeg;base64,' + base64Images[profile._id]" class="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" [class.blur-md]="!isLoggedIn">
            </ng-container>
            <ng-template #defSim>
                <div class="h-full w-full flex items-center justify-center bg-surface-container-low">
                  <span class="material-symbols-outlined text-[36px] text-on-surface-variant/30" [class.blur-sm]="!isLoggedIn">person</span>
                </div>
            </ng-template>
          </div>
          <div class="p-2.5 flex-1 flex flex-col">
            <h3 class="font-display font-bold text-xs text-primary m-0 truncate">
              {{ isLoggedIn ? (profile.basic_info?.first_name + ' ' + profile.basic_info?.last_name) : (profile.basic_info?.first_name + ' ' + profile.basic_info?.last_name?.charAt(0) + '.') }}
            </h3>
            <p class="text-[10px] text-secondary font-medium m-0 mt-0.5 truncate">
              {{ getAge(profile.basic_info?.date_of_birth) }} Yrs • {{ profile.personal_info?.height ? profile.personal_info.height + ' cm' : '--' }}
            </p>
            <p class="text-[9px] text-secondary/80 mt-0.5 mb-0 truncate">
              {{ (profile.professional_info?.occupation_id?.occupation_nme || profile.professional_info?.occupation_id) || 'Professional' }}
            </p>
          </div>
        </a>
      </div>
    </div>

  </main>
</div>

<!-- Report Modal -->
<div *ngIf="showReportModal" style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); backdrop-filter: blur(2px); display: flex; align-items: center; justify-content: center; z-index: 9999; padding: 16px;">
  <div style="background: white; border-radius: 20px; max-w: 400px; w: 100%; box-shadow: 0 10px 25px rgba(0,0,0,0.1); padding: 24px; border: 1px solid #f0f0f0;">
    <div style="display: flex; align-items: center; justify-content: space-between; border-b: 1px solid #f0f0f0; padding-bottom: 12px; margin-bottom: 16px;">
      <h3 style="margin: 0; font-size: 16px; font-weight: bold; color: #b01e3e; display: flex; align-items: center; gap: 6px;">
        <span class="material-symbols-outlined" style="font-size: 20px; color: #dc3545;">report</span>
        Report Profile
      </h3>
      <button (click)="closeReportModal()" style="background: none; border: none; cursor: pointer; color: #666; display: flex; align-items: center; justify-content: center;">
        <span class="material-symbols-outlined" style="font-size: 18px;">close</span>
      </button>
    </div>

    <form (ngSubmit)="submitReport()">
      <div style="margin-bottom: 16px;">
        <label style="display: block; font-size: 11px; font-weight: bold; color: #666; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px;">Reason for reporting</label>
        <select [(ngModel)]="reportReason" name="reason" style="width: 100%; border-radius: 10px; border: 1px solid #ddd; padding: 10px; font-size: 12px; background: white; outline: none;" required>
          <option value="" disabled selected>Select a reason</option>
          <option value="Fake Profile">Fake Profile</option>
          <option value="Wrong Information">Wrong Information</option>
          <option value="Inappropriate Content">Inappropriate Content</option>
          <option value="Harassment">Harassment</option>
          <option value="Spam">Spam</option>
          <option value="Other">Other</option>
        </select>
      </div>

      <div style="margin-bottom: 16px;">
        <label style="display: block; font-size: 11px; font-weight: bold; color: #666; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px;">Description / Details</label>
        <textarea [(ngModel)]="reportDescription" name="description" placeholder="Please provide additional details about the issue (minimum 10 characters)..." style="width: 100%; border-radius: 10px; border: 1px solid #ddd; padding: 12px; font-size: 12px; outline: none; height: 90px; resize: none;" required></textarea>
        <div style="display: flex; justify-content: flex-end; font-size: 11px; margin-top: 4px; font-weight: 500;" [style.color]="(reportDescription || '').trim().length < 10 ? '#dc3545' : '#198754'">
          {{ (reportDescription || '').trim().length }} / 10 min characters
        </div>
      </div>

      <div style="display: flex; justify-content: flex-end; gap: 8px; margin-top: 20px;">
        <button type="button" (click)="closeReportModal()" style="font-size: 12px; font-weight: bold; padding: 8px 16px; border-radius: 10px; border: 1px solid #ddd; background: white; cursor: pointer;" [disabled]="submittingReport">Cancel</button>
        <button type="submit" style="background: #dc3545; color: white; border: none; font-size: 12px; font-weight: bold; padding: 8px 16px; border-radius: 10px; cursor: pointer; display: flex; align-items: center; gap: 4px; transition: opacity 0.2s;" [disabled]="submittingReport" [style.opacity]="submittingReport ? '0.6' : '1'" [style.cursor]="submittingReport ? 'not-allowed' : 'pointer'">
          <span *ngIf="submittingReport" class="material-symbols-outlined" style="font-size: 14px; animation: spin 1s linear infinite;">sync</span>
          Submit Report
        </button>
      </div>
    </form>
  </div>
</div>
